import {
  ADMIN_ROLES,
  ADVISOR_FUNCTION_LABELS,
  AdvisorFunction,
  assignableRoles,
  highestRole,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  ROLE_RANK,
  Role,
  type HandoverCompanyDto,
  type MembershipInput,
  type UpdateUserPayload,
  type UserAdminDto,
} from '@performance-crm/shared';
import { KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { fmt } from '@/lib/format';
import {
  useAdminUsers,
  useCreateUser,
  useHandoverPreview,
  useProjects,
  useResetPassword,
  useUpdateUser,
} from '@/lib/queries';

/** Empresas que administra quien mira (ADMIN / GERENTE), incluidas las ocultas, con sus roles. */
function useAdminCompanies() {
  const { user: me } = useAuth();
  const byCompany = new Map<string, { companyId: string; companyName: string; hidden: boolean }>();
  const roles = new Map<string, Role[]>();
  for (const m of me?.memberships ?? []) {
    roles.set(m.companyId, [...(roles.get(m.companyId) ?? []), m.role]);
    if (ADMIN_ROLES.includes(m.role) && !byCompany.has(m.companyId))
      byCompany.set(m.companyId, {
        companyId: m.companyId,
        companyName: m.companyName,
        hidden: m.companyActive === false,
      });
  }
  return {
    me,
    companies: [...byCompany.values()],
    rolesIn: (companyId: string) => roles.get(companyId) ?? [],
  };
}

const soleAdminTitle = (u: UserAdminDto) =>
  u.soleAdminOf.length
    ? `Es el único administrador de ${u.soleAdminOf.map((c) => c.companyName).join(', ')}: nombra otro antes de quitarle el rol o desactivarlo`
    : undefined;

export function UsersTab({ companyId }: { companyId: string }) {
  const [q, setQ] = useState('');
  const { data: users, isLoading } = useAdminUsers({ companyId, q: q.length >= 2 ? q : undefined });
  const update = useUpdateUser();
  const toast = useToast();
  const { user: me } = useAuth();
  const [editing, setEditing] = useState<UserAdminDto | 'new' | null>(null);
  const [resetFor, setResetFor] = useState<UserAdminDto | null>(null);
  const [deactivating, setDeactivating] = useState<UserAdminDto | null>(null);
  // Usuario ya inactivo con leads a su nombre (p. ej. un traspaso interrumpido): «Traspasar leads».
  const [handingOver, setHandingOver] = useState<UserAdminDto | null>(null);
  return (
    <div className="space-y-3">
      <div className="bg-card flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nombre, usuario o correo…"
          aria-label="Buscar usuario"
          className="h-9 w-64"
        />
        <Button
          size="sm"
          className="ml-auto"
          onClick={() => setEditing('new')}
          data-testid="btn-new-user"
        >
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {users && (
        <div className="bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2">Usuario</th>
                <th className="px-3 py-2">Correo</th>
                <th className="px-3 py-2">Roles</th>
                <th className="px-3 py-2">Función</th>
                <th className="px-3 py-2">Último acceso</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className={`border-t ${!u.active ? 'opacity-60' : ''}`}
                  data-testid="user-row"
                >
                  <td className="px-3 py-2">
                    <p className="font-medium">{u.fullName}</p>
                    <p className="text-muted-foreground text-xs">
                      @{u.username}
                      {u.kind === 'BOT' ? ' · bot' : ''}
                      {u.isTestAccount ? ' · prueba' : ''}
                    </p>
                  </td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.memberships.map((m) => (
                        <Badge
                          key={m.id}
                          tone={m.companyId === companyId ? 'primary' : 'outline'}
                          title={ROLE_DESCRIPTIONS[m.role]}
                        >
                          {ROLE_LABELS[m.role] ?? m.role}
                          {m.projectName ? ` · ${m.projectName}` : ''}
                          {m.companyId !== companyId ? ` (${m.companyName})` : ''}
                        </Badge>
                      ))}
                      {u.soleAdminOf.length > 0 && (
                        <Badge tone="warning" title={soleAdminTitle(u)}>
                          único administrador
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {u.advisorFunction ? ADVISOR_FUNCTION_LABELS[u.advisorFunction] : '—'}
                  </td>
                  <td className="px-3 py-2">{u.lastLoginAt ? fmt.ago(u.lastLoginAt) : 'nunca'}</td>
                  <td className="px-3 py-2">
                    <Badge tone={u.active ? 'success' : 'muted'}>
                      {u.active ? 'activo' : 'inactivo'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {u.kind === 'HUMAN' && (
                        <>
                          {/* Las acciones que el servidor rechazaría se muestran apagadas con el motivo. */}
                          {!u.canManage && u.manageBlockedReason && (
                            <span
                              className="text-muted-foreground text-xs"
                              data-testid="user-manage-blocked"
                            >
                              {u.manageBlockedReason}
                            </span>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Editar usuario"
                            disabled={!u.canManage}
                            title={u.manageBlockedReason ?? undefined}
                            onClick={() => setEditing(u)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Cambiar contraseña"
                            disabled={!u.canManage}
                            title={u.manageBlockedReason ?? undefined}
                            onClick={() => setResetFor(u)}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          {u.id !== me?.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!u.canManage || u.soleAdminOf.length > 0}
                              title={u.manageBlockedReason ?? soleAdminTitle(u)}
                              data-testid={u.active ? 'user-deactivate' : 'user-activate'}
                              onClick={() => {
                                // Desactivar pasa por el diálogo de traspaso (Lote 6 · I2, P7): qué deja y a quién.
                                if (u.active) {
                                  setDeactivating(u);
                                  return;
                                }
                                update
                                  .mutateAsync({ id: u.id, input: { active: true } })
                                  .then(() => toast.success('Usuario activado'))
                                  .catch((e: Error) =>
                                    toast.error('No se pudo actualizar', e.message),
                                  );
                              }}
                            >
                              {u.active ? 'Desactivar' : 'Activar'}
                            </Button>
                          )}
                          {!u.active && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!u.canManage}
                              title={
                                u.manageBlockedReason ??
                                'Pasa a otro asesor o a la bolsa los leads activos que aún tenga a su nombre'
                              }
                              data-testid="user-handover"
                              onClick={() => setHandingOver(u)}
                            >
                              Traspasar leads
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <UserDialog
          companyId={companyId}
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {resetFor && <PasswordDialog user={resetFor} onClose={() => setResetFor(null)} />}
      {deactivating && (
        <DeactivateDialog user={deactivating} onClose={() => setDeactivating(null)} />
      )}
      {handingOver && (
        <DeactivateDialog user={handingOver} mode="handover" onClose={() => setHandingOver(null)} />
      )}
    </div>
  );
}

type HandoverChoice = Record<string, { to: 'POOL' | string }>;

/** «Alfa: 3 leads a Juan Pérez · quitado de 1 regla» por empresa, solo con lo que pasó, para el aviso al guardar. */
function describeHandover(result: UserAdminDto['handover']) {
  if (!result) return undefined;
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  const lines = result.companies
    .map((c) => {
      const parts: string[] = [];
      if (c.leads > 0)
        parts.push(
          `${n(c.leads, 'lead', 'leads')} ${c.to === 'POOL' ? 'a la bolsa' : `a ${c.toName ?? 'otro asesor'}`}`,
        );
      if (c.rules > 0)
        parts.push(`quitado de ${n(c.rules, 'regla de reparto', 'reglas de reparto')}`);
      if (c.emptyRules > 0)
        parts.push(
          `${n(c.emptyRules, 'regla por turnos quedó', 'reglas por turnos quedaron')} sin asesores (los leads nuevos van a la bolsa)`,
        );
      return parts.length ? `${c.companyName}: ${parts.join(' · ')}` : '';
    })
    .filter(Boolean);
  return lines.length ? lines.join(' · ') : undefined;
}

/**
 * Desactivar (o «Traspasar leads» de un usuario ya inactivo): carga la vista previa (leads, tareas, reglas por
 * empresa) y pide el destino antes de guardar. En modo `handover` solo viaja el traspaso, sin tocar `active`.
 */
function DeactivateDialog({
  user,
  mode = 'deactivate',
  onClose,
}: {
  user: UserAdminDto;
  mode?: 'deactivate' | 'handover';
  onClose: () => void;
}) {
  const { data, isLoading, error } = useHandoverPreview(user.id);
  const update = useUpdateUser();
  const toast = useToast();
  const handoverOnly = mode === 'handover';
  return (
    <HandoverDialog
      title={
        handoverOnly ? `Traspasar los leads de ${user.fullName}` : `Desactivar a ${user.fullName}`
      }
      intro={
        handoverOnly
          ? 'Está dado de baja, pero aún puede tener leads activos a su nombre (por ejemplo si un traspaso se interrumpió). Lo que tiene pasa a quien elijas:'
          : 'Ya no podrá entrar al CRM ni recibir leads. Lo que tiene a su nombre pasa a quien elijas:'
      }
      confirmLabel={handoverOnly ? 'Traspasar' : 'Desactivar'}
      companies={data?.companies}
      loadError={error ? (error as Error).message : null}
      loading={isLoading}
      pending={update.isPending}
      onClose={onClose}
      onConfirm={async (handover) => {
        const r = await update.mutateAsync({
          id: user.id,
          input: handoverOnly ? { handover } : { active: false, handover },
        });
        const detail = describeHandover(r.handover);
        toast.success(
          handoverOnly
            ? detail
              ? 'Leads traspasados'
              : 'No había leads activos a su nombre'
            : 'Usuario desactivado',
          detail,
        );
        onClose();
      }}
    />
  );
}

function HandoverDialog({
  title,
  intro,
  confirmLabel,
  companies,
  loading,
  loadError,
  pending,
  onClose,
  onConfirm,
}: {
  title: string;
  intro: string;
  confirmLabel: string;
  companies: HandoverCompanyDto[] | undefined;
  loading?: boolean;
  loadError?: string | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (handover: HandoverChoice) => Promise<void>;
}) {
  const toast = useToast();
  // Destino por empresa: la bolsa por defecto (regla de oro: ningún lead queda a nombre de un inactivo).
  const [choice, setChoice] = useState<Record<string, string>>({});
  const needing = (companies ?? []).filter((c) => c.leads > 0);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const handover: HandoverChoice = {};
    for (const c of needing) handover[c.companyId] = { to: choice[c.companyId] || 'POOL' };
    try {
      await onConfirm(handover);
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <form onSubmit={(e) => void submit(e)} className="space-y-4" data-testid="handover-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{intro}</DialogDescription>
          </DialogHeader>
          {loading && <Skeleton className="h-24 w-full" />}
          {loadError && <p className="text-danger text-sm">{loadError}</p>}
          {companies && companies.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No tiene leads activos ni figura en reglas de reparto en las empresas que administras.
            </p>
          )}
          {companies?.map((c) => (
            <div
              key={c.companyId}
              className="space-y-2 rounded-md border p-3 text-sm"
              data-testid="handover-company"
            >
              <p>
                <span className="font-medium">{c.companyName}</span>: tiene{' '}
                <span className="font-medium">
                  {plural(c.leads, 'lead activo', 'leads activos')}
                </span>{' '}
                y {plural(c.tasks, 'tarea pendiente', 'tareas pendientes')}; está en{' '}
                {plural(c.rules, 'regla de reparto', 'reglas de reparto')}.
                {c.rules > 0 && ' Al guardar sale de esas reglas.'}
              </p>
              {c.leads > 0 && (
                <div className="space-y-1">
                  <Label htmlFor={`handover-${c.companyId}`}>
                    ¿A quién pasan sus leads y tareas pendientes?
                  </Label>
                  <Select
                    id={`handover-${c.companyId}`}
                    value={choice[c.companyId] ?? 'POOL'}
                    onChange={(e) => setChoice((x) => ({ ...x, [c.companyId]: e.target.value }))}
                    data-testid="handover-target"
                  >
                    <option value="POOL">
                      A la bolsa (sin asesor, para que el coordinador los reparta)
                    </option>
                    {c.candidates.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.fullName}
                        {a.role !== 'ASESOR' ? ` · ${ROLE_LABELS[a.role] ?? a.role}` : ''}
                      </option>
                    ))}
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    El destino recibe un solo aviso con el total; quienes coordinan reciben el
                    resumen. Los leads cerrados se quedan a su nombre para el historial.
                  </p>
                </div>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={pending || loading || !companies}
              data-testid="handover-confirm"
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({
  companyId,
  user,
  onClose,
}: {
  companyId: string;
  user: UserAdminDto | null;
  onClose: () => void;
}) {
  const create = useCreateUser();
  const update = useUpdateUser();
  const toast = useToast();
  const { me, companies, rolesIn } = useAdminCompanies();
  const { data: projects } = useProjects();
  const adminIds = companies.map((c) => c.companyId);
  // Un cambio de rol que deja leads sin quien los atienda (400 HANDOVER_REQUIRED) abre el diálogo de traspaso
  // con el mismo cuerpo, para reenviarlo con el destino elegido.
  const [handover, setHandover] = useState<{
    companies: HandoverCompanyDto[];
    input: UpdateUserPayload;
  } | null>(null);
  const [f, setF] = useState({
    username: user?.username ?? '',
    email: user?.email ?? '',
    password: '',
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    phone: user?.phone ?? '',
    advisorFunction: user?.advisorFunction ?? '',
    isTestAccount: user?.isTestAccount ?? false,
  });
  // La API ya devuelve solo las membresías de las empresas que administro (ocultas incluidas).
  const [memberships, setMemberships] = useState<MembershipInput[]>(
    user
      ? user.memberships
          .filter((m) => adminIds.includes(m.companyId))
          .map((m) => ({ companyId: m.companyId, projectId: m.projectId, role: m.role }))
      : [{ companyId, projectId: null, role: 'ASESOR' }],
  );
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));
  const setM = (i: number, patch: Partial<MembershipInput>) =>
    setMemberships((ms) => ms.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const isSoleAdminRow = (m: MembershipInput) =>
    m.role === 'ADMIN' && !!user?.soleAdminOf.some((c) => c.companyId === m.companyId);
  const roleOptions = (m: MembershipInput) => {
    const options = assignableRoles(rolesIn(m.companyId));
    return options.includes(m.role) ? options : [m.role, ...options];
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (user) {
        // Empresas en las que tenía membresía y ya no queda ninguna fila: se retira de ellas.
        const removeCompanyIds = [
          ...new Set(
            user.memberships
              .map((m) => m.companyId)
              .filter((c) => adminIds.includes(c) && !memberships.some((m) => m.companyId === c)),
          ),
        ];
        if (user.id === me?.id) {
          const lowered = companies.filter((c) => {
            const before = highestRole(rolesIn(c.companyId));
            const after = highestRole(
              memberships.filter((m) => m.companyId === c.companyId).map((m) => m.role),
            );
            return before && (!after || ROLE_RANK[after] < ROLE_RANK[before]);
          });
          if (
            lowered.length &&
            !confirm(
              `Vas a bajar tus propios permisos en ${lowered.map((c) => c.companyName).join(', ')}. Después de guardar quizá ya no puedas administrar esa empresa. ¿Continuar?`,
            )
          )
            return;
        }
        const input: UpdateUserPayload = {
          email: f.email,
          firstName: f.firstName,
          lastName: f.lastName,
          phone: f.phone || null,
          advisorFunction: (f.advisorFunction || null) as AdvisorFunction | null,
          isTestAccount: f.isTestAccount,
          memberships,
          ...(removeCompanyIds.length ? { removeCompanyIds } : {}),
        };
        try {
          await update.mutateAsync({ id: user.id, input });
        } catch (err) {
          const details =
            err instanceof ApiRequestError
              ? (err.body.details as
                  { code?: string; companies?: HandoverCompanyDto[] } | undefined)
              : undefined;
          if (details?.code === 'HANDOVER_REQUIRED' && details.companies) {
            setHandover({ companies: details.companies, input });
            return;
          }
          throw err;
        }
      } else
        await create.mutateAsync({
          username: f.username,
          email: f.email,
          password: f.password,
          firstName: f.firstName,
          lastName: f.lastName,
          phone: f.phone || null,
          advisorFunction: (f.advisorFunction || null) as AdvisorFunction | null,
          isTestAccount: f.isTestAccount,
          memberships,
        });
      toast.success(user ? 'Usuario actualizado' : 'Usuario creado');
      onClose();
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{user ? `Editar ${user.fullName}` : 'Nuevo usuario'}</DialogTitle>
            <DialogDescription>
              Roles por empresa (y opcionalmente por proyecto). Un gerente no puede nombrar
              administradores ni modificar a uno.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="us-username">Usuario</Label>
              <Input
                id="us-username"
                value={f.username}
                onChange={(e) => set({ username: e.target.value })}
                required
                disabled={!!user}
                pattern="[a-z0-9._-]+"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="us-email">Correo</Label>
              <Input
                id="us-email"
                type="email"
                value={f.email}
                onChange={(e) => set({ email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="us-first">Nombres</Label>
              <Input
                id="us-first"
                value={f.firstName}
                onChange={(e) => set({ firstName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="us-last">Apellidos</Label>
              <Input
                id="us-last"
                value={f.lastName}
                onChange={(e) => set({ lastName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="us-phone">Celular</Label>
              <Input
                id="us-phone"
                value={f.phone}
                onChange={(e) => set({ phone: e.target.value })}
              />
            </div>
            {!user && (
              <div className="space-y-1">
                <Label htmlFor="us-password">Contraseña inicial</Label>
                <Input
                  id="us-password"
                  type="password"
                  value={f.password}
                  onChange={(e) => set({ password: e.target.value })}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="us-fn">Función comercial</Label>
              <Select
                id="us-fn"
                value={f.advisorFunction}
                onChange={(e) => set({ advisorFunction: e.target.value })}
              >
                <option value="">—</option>
                {Object.values(AdvisorFunction).map((v) => (
                  <option key={v} value={v}>
                    {ADVISOR_FUNCTION_LABELS[v]}
                  </option>
                ))}
              </Select>
              <p className="text-muted-foreground text-xs">
                Solo informativa: se muestra junto al nombre en Equipo. No cambia permisos ni
                reparto.
              </p>
            </div>
            <label className="flex items-start gap-2 self-end text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={f.isTestAccount}
                onChange={(e) => set({ isTestAccount: e.target.checked })}
                className="mt-0.5 h-4 w-4"
              />
              <span>
                Cuenta de prueba
                <span className="text-muted-foreground block text-xs">
                  Queda fuera de rankings, del reparto de leads y de todas las métricas. Úsala solo
                  para probar.
                </span>
              </span>
            </label>
          </div>
          <div className="space-y-2">
            <Label>Membresías</Label>
            {memberships.map((m, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                <Select
                  value={m.companyId}
                  onChange={(e) => setM(i, { companyId: e.target.value, projectId: null })}
                  aria-label="Empresa de la membresía"
                  disabled={isSoleAdminRow(m)}
                >
                  {companies.map((c) => (
                    <option key={c.companyId} value={c.companyId}>
                      {c.companyName}
                      {c.hidden ? ' (oculta)' : ''}
                    </option>
                  ))}
                </Select>
                <Select
                  value={m.projectId ?? ''}
                  onChange={(e) => setM(i, { projectId: e.target.value || null })}
                  aria-label="Proyecto de la membresía"
                >
                  <option value="">Toda la empresa</option>
                  {projects
                    ?.filter((p) => p.companyId === m.companyId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </Select>
                <Select
                  value={m.role}
                  onChange={(e) => setM(i, { role: e.target.value as Role })}
                  aria-label="Rol"
                  disabled={isSoleAdminRow(m)}
                  title={
                    isSoleAdminRow(m)
                      ? 'Es el único administrador de esta empresa: nombra otro antes de cambiarle el rol'
                      : ROLE_DESCRIPTIONS[m.role]
                  }
                >
                  {roleOptions(m).map((r) => (
                    <option key={r} value={r} title={ROLE_DESCRIPTIONS[r]}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="Quitar membresía"
                  disabled={memberships.length === 1 || isSoleAdminRow(m)}
                  title={
                    isSoleAdminRow(m)
                      ? 'Es el único administrador de esta empresa: nombra otro antes de quitarlo'
                      : undefined
                  }
                  onClick={() => setMemberships((ms) => ms.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <button
              type="button"
              className="text-primary text-xs underline"
              onClick={() =>
                setMemberships((ms) => [
                  ...ms,
                  {
                    companyId: adminIds.includes(companyId) ? companyId : adminIds[0]!,
                    projectId: null,
                    role: 'ASESOR',
                  },
                ])
              }
            >
              + otra membresía
            </button>
            <details className="text-muted-foreground text-xs">
              <summary className="cursor-pointer">¿Qué puede hacer cada rol?</summary>
              <ul className="mt-1 space-y-0.5">
                {Object.values(Role)
                  .filter((r) => r !== 'BOT')
                  .map((r) => (
                    <li key={r}>
                      <span className="text-foreground font-medium">{ROLE_LABELS[r]}:</span>{' '}
                      {ROLE_DESCRIPTIONS[r]}
                    </li>
                  ))}
              </ul>
            </details>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || update.isPending}
              data-testid="user-save"
            >
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {user && handover && (
        <HandoverDialog
          title={`Cambio de rol de ${user.fullName}`}
          intro="Con el nuevo rol deja de poder atender algunos de sus leads. Lo que tiene a su nombre pasa a quien elijas:"
          confirmLabel="Guardar con traspaso"
          companies={handover.companies}
          pending={update.isPending}
          onClose={() => setHandover(null)}
          onConfirm={async (choice) => {
            const r = await update.mutateAsync({
              id: user.id,
              input: { ...handover.input, handover: choice },
            });
            toast.success('Usuario actualizado', describeHandover(r.handover));
            setHandover(null);
            onClose();
          }}
        />
      )}
    </Dialog>
  );
}

function PasswordDialog({ user, onClose }: { user: UserAdminDto; onClose: () => void }) {
  const reset = useResetPassword();
  const toast = useToast();
  const [password, setPassword] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reset
              .mutateAsync({ id: user.id, password })
              .then(() => {
                toast.success('Contraseña cambiada', 'Sus sesiones anteriores quedaron cerradas.');
                onClose();
              })
              .catch((err: Error) => toast.error('No se pudo cambiar', err.message));
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Nueva contraseña · {user.fullName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="pw-new">Contraseña (mín. 8, letras y números)</Label>
            <Input
              id="pw-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={reset.isPending}>
              Cambiar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
